import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { NostroLadderData, NostroLadderProjectionData } from './nostro-ladder-data';
import { AppConfig } from './../../app.config';
import { SnapshotService } from './../../snapshot/snapshot.service';
import {
    ACCOUNT_FLOW_TYPE_AUTHORIZED,
    ACCOUNT_FLOW_TYPE_PROJECTED,
    ACCOUNT_FLOW_TYPE_ACTUAL,
    ACCOUNT_FLOW_TYPE_BALANCE,
    ACCOUNT_FLOW_TYPE_SWEEP_INSTRUCTION,
    AccountFlow
} from '@liquitics/transaction';
import { CORRECTION_TYPES } from '@liquitics/transaction';
import {
    GetSurvivalHorizonServiceDataOutput,
} from '@liquitics/transaction';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { SurvivalHorizonService } from './../survival-horizon.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { viewPortHeightToPX } from '../../common/render-util';

const PROJECTION_UPDATES_ACCOUNT = 'Projection Updates';

@Component({
    selector: 'app-nostro-ladder',
    templateUrl: './nostro-ladder.component.html',
    styleUrls: ['./nostro-ladder.component.css']
})
export class NostroLadderComponent implements OnInit, OnDestroy {

    private dataStreamDisposable: Subscription;
    subscription: Subscription;
    tableData: any[];
    showTable = false;
    currency: string;
    vpHeightToPx = viewPortHeightToPX;

    constructor(private snapshotService: SnapshotService,
        private survivalHorizonService: SurvivalHorizonService,
        private dashboardEventService: DashboardEventService,
        private appConfig: AppConfig) { }

    ngOnInit() {
        this.dataStreamDisposable = this.survivalHorizonService.getPollingDataSubject()
            .subscribe({
                next: (data: GetSurvivalHorizonServiceDataOutput) => {
                    this.refreshFromData(data);
                }, error: (err) => {
                    console.log(err);
                    throw err;
                }
            });
        // setTimeout(() => {
        // this.showTable = (this.tableData && this.tableData.length > 0);
        // }, 1000);
        this.subscription = (this.dashboardEventService.currencySelectionSubject.subscribe({
            next: currency => {
                this.currency = currency;
                const currentRequest = this.survivalHorizonService.buildPollingRequestParams();
                this.showTable = false;
                this.survivalHorizonService.getDataObservable(currentRequest)
                    .pipe(take(1))
                    .toPromise()
                    .then((data: GetSurvivalHorizonServiceDataOutput) => {
                        this.refreshFromData(data);
                    })
            }
        }));
    }

    private refreshFromData(data: GetSurvivalHorizonServiceDataOutput) {
        let otherAccountNLD;
        if (!data.accountFlows) {
            return;
        }
        const currency = this.currency || this.appConfig.activeUserProfile.getCurrencies()[0]

        // Hopefully this will resolve itself when moving off of hand created files,
        // but the account contains a break charater at the end (probably because its the last field in the csv)
        data.accountFlows.forEach((af: AccountFlow) => {
            af.account = af.account.trim();
        });

        const nostroData: NostroLadderData[] = data.accountFlows
            .filter((af: AccountFlow) => {
                return af.type === ACCOUNT_FLOW_TYPE_BALANCE;
            })
            .map((af: AccountFlow) => {
                const nld = new NostroLadderData();
                nld.currency = af.currency;
                nld.account = af.account;
                nld.openingBalance = af.amount;
                nld.openingExpectation = 0;
                nld.runningBalance = nld.openingBalance;
                nld.runningProjected = nld.openingBalance + nld.openingExpectation;
                return nld;
            });
        const nldByAccount: Map<string, NostroLadderData> = new Map();
        nostroData.forEach((nld: NostroLadderData) => {
            nldByAccount.set(nld.account, nld);
        });
        const startingProjections: NostroLadderProjectionData[] = data.accountFlows
            .filter((af: AccountFlow) => {
                return af.type === ACCOUNT_FLOW_TYPE_PROJECTED;
            })
            .map((af: AccountFlow) => {
                const nlpd = new NostroLadderProjectionData();
                nlpd.account = af.account;
                nlpd.clientGroup = af.clientGroup;
                nlpd.product = af.product;
                nlpd.currency = af.currency;
                nlpd.startBalance = af.amount;
                return nlpd;
            })
        startingProjections.forEach((pd: NostroLadderProjectionData) => {
            const nld: NostroLadderData = nldByAccount.get(pd.account);
            if (nld) {
                nld.addProjectionData(pd);
            }
        });

        data.accountFlows.filter((af: AccountFlow) => {
            return af.type === ACCOUNT_FLOW_TYPE_AUTHORIZED || af.type === ACCOUNT_FLOW_TYPE_SWEEP_INSTRUCTION;
        })
            .forEach((af: AccountFlow) => {
                let nld: NostroLadderData = nldByAccount.get(af.account);
                if (!nld) {
                    nld = new NostroLadderData();
                    nld.currency = af.currency;
                    nld.account = af.account;
                    nld.openingBalance = 0
                    nld.openingExpectation = 0;
                    nld.runningBalance = 0;
                    nld.runningProjected = af.amount;
                    nldByAccount.set(nld.account, nld);
                    nostroData.push(nld);
                } else {
                    const pd: NostroLadderProjectionData = nld.getProjectionData(af.clientGroup, af.product);
                    if (pd) {
                        let excess;
                        if (pd.startBalance >= 0) {
                            excess = Math.max(af.amount - pd.startBalance, 0);
                        } else {
                            excess = Math.min(af.amount - pd.startBalance, 0);
                        }
                        nld.runningProjected += excess;
                    } else {
                        nld.runningProjected += af.amount;
                    }
                }
            });
        data.accountCorrections.filter((ac: any) => {
            return ac.type === CORRECTION_TYPES.PROJECTION_UPDATE || ac.type === CORRECTION_TYPES.BALANCE_CORRECTION;
        })
            .forEach((ac: any) => {
                let nld: NostroLadderData;
                if (!ac.account) {
                    // should never come here for balance correction
                    if (!otherAccountNLD) {
                        otherAccountNLD = new NostroLadderData();
                        otherAccountNLD.currency = ac.currency;
                        otherAccountNLD.account = PROJECTION_UPDATES_ACCOUNT;
                        otherAccountNLD.openingBalance = 0.0;
                        otherAccountNLD.openingExpectation = 0.0;
                        otherAccountNLD.runningBalance = 0.0;
                        otherAccountNLD.runningProjected = 0.0;
                        nostroData.push(otherAccountNLD);
                    }
                    nld = otherAccountNLD;
                } else {
                    nld = nldByAccount.get(ac.account);
                }
                if (nld) {
                    nld.runningProjected += ac.amount;
                    if (ac.type === CORRECTION_TYPES.BALANCE_CORRECTION) {
                        nld.runningBalance += ac.amount;
                    }
                }
            });

        data.accountFlows.filter((af: AccountFlow) => {
            return af.type === ACCOUNT_FLOW_TYPE_ACTUAL;
        })
            .forEach((af: AccountFlow) => {
                let nld: NostroLadderData = nldByAccount.get(af.account);
                if (!nld) {
                    nld = new NostroLadderData();
                    nld.currency = af.currency;
                    nld.account = af.account;
                    nld.openingBalance = 0
                    nld.openingExpectation = 0;
                    nld.runningBalance = af.amount;
                    nld.runningProjected = 0;
                    nldByAccount.set(nld.account, nld);
                    nostroData.push(nld);
                } else {
                    nld.runningBalance += af.amount;
                }
            });
        const accountNames = this.snapshotService.applicationConfiguration.accountNames;
        this.tableData = nostroData
            .map((r) => {
                if (accountNames[r.account]) {
                    r.account = accountNames[r.account];
                } else {
                    if (r.account !== PROJECTION_UPDATES_ACCOUNT) {
                        // r.account = null;
                    }
                }
                return r;
            })
            // .filter((r) => {
            //     return (r.account !== null);
            // })
            ;
        this.showTable = true;
    }

    ngOnDestroy() {
        if (this.dataStreamDisposable) {
            this.dataStreamDisposable.unsubscribe();
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}

